import {
  MibCaseVocabulary,
  MibFilterChip,
  MibPalette,
  provideMibCaseVocabulary,
} from '@medinbox/angular';
import { VideoCategory } from '@shared/enums/video-category.enum';

/** How a category reads and how it is coloured, on a badge and on the chip that filters it. */
interface CaseCategoryLook {
  readonly label: string;
  readonly palette: MibPalette;
}

/**
 * The five categories, A to Z by label then Other: every list on screen follows this order.
 * The label is stored because every rule that turns `ep` into a word gives `Ep`.
 */
export const CASE_CATEGORIES: Record<VideoCategory, CaseCategoryLook> = {
  [VideoCategory.Coronary]: { label: 'Coronary', palette: 'red' },
  [VideoCategory.EP]: { label: 'EP', palette: 'orange' },
  [VideoCategory.Peripheral]: { label: 'Peripheral', palette: 'green' },
  [VideoCategory.Structural]: { label: 'Structural', palette: 'indigo' },
  [VideoCategory.Other]: { label: 'Other', palette: 'grey' },
};

export const CASE_CATEGORY_IDS = Object.keys(CASE_CATEGORIES) as readonly VideoCategory[];

/**
 * In declared order, so a category sits in the same place from one card to the next.
 * Plain strings: a newer API could send a sixth word, and an unknown one is dropped.
 */
export function caseCategoryLabels(categories: readonly string[] | undefined): string[] {
  const sent = new Set((categories ?? []).map((category) => category.trim().toLowerCase()));

  return CASE_CATEGORY_IDS.filter((category) => sent.has(category)).map(
    (category) => CASE_CATEGORIES[category].label,
  );
}

/** The chip of the favorites, among the categories of the row. */
export const FAVOURITES_CHIP = 'favourites';

/** The row: All, the categories from the badges' table, then the favorites. */
export const CASE_CATEGORY_CHIPS: MibFilterChip[] = [
  { id: 'all', label: 'All', all: true },
  ...CASE_CATEGORY_IDS.map((category) => ({
    id: category,
    label: CASE_CATEGORIES[category].label,
    palette: CASE_CATEGORIES[category].palette,
  })),
  { id: FAVOURITES_CHIP, label: 'Favorites', icon: 'star', pressedIcon: 'favourite' },
];

/** Only what is worth a glance: a failure, never the ordinary. The tooltip is how it is read. */
export const CASE_FLAGS = {
  published: {
    icon: 'user-check',
    theme: 'success',
    tooltip: 'Published within your organization',
  },
  phi: {
    icon: 'shield',
    theme: 'danger',
    tooltip: 'Protected health information detected',
  },
  mediaFailed: {
    icon: 'triangle-alert',
    theme: 'warning',
    tooltip: 'The video could not be prepared',
  },
  chatFailed: {
    icon: 'triangle-alert',
    theme: 'warning',
    tooltip: 'The analysis could not be run',
  },
} as const satisfies MibCaseVocabulary['flags'];

/** Puts the vocabulary in force for the cards underneath. */
export const provideCaseVocabulary = () =>
  provideMibCaseVocabulary({
    categories: Object.fromEntries(
      CASE_CATEGORY_IDS.map((category) => [
        category,
        { palette: CASE_CATEGORIES[category].palette },
      ]),
    ),
    flags: CASE_FLAGS,
  });
